import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { NavLink } from "react-router-dom";
import { db } from "../FIREBASE/firebase";
import routeUrls from "../COMPONENTS/navbar/navLink";
import { usePageBackground } from "../COMPONENTS/usePageBackground";
import {
  Home,
  Wall,
  WindowOpening,
  WallColors,
  applyPaint,
  Room,
  Fixture,
  Measure,
  TextLabel,
  FIXTURE_KINDS,
  DRAWN_KINDS,
  NO_BOX,
  OVERLAY,
  kindOf,
  abbrForFixture,
  blankFixture,
  normalizeHome,
  detectRooms,
  openWallEnds,
  extendedWalls,
  fileToDataUrl,
  newId,
  snap,
  dueDate,
  replacementStatus,
  DueStatus,
  PLAN_W,
  PLAN_H,
  ELEV_KINDS,
  elevOf,
} from "../COMPONENTS/home/home";
import { Moodboards } from "../COMPONENTS/home/Moodboards";
import { detectWindows } from "../COMPONENTS/home/detectWindows";
import "../CSS/hem.scss";

// three.js is large — the 3D view loads in its own chunk on first open
const Hem3D = React.lazy(() => import("../COMPONENTS/home/Hem3D"));
type View3D = "off" | "split" | "full";
type PaintChange = { wallId: string; side: "p" | "m"; a: number; b: number };

const STATUS_LABEL: Record<DueStatus, string> = { none: "", ok: "OK", soon: "Snart", overdue: "Försenad" };
const fmtMonth = (d: Date): string => d.toLocaleDateString("sv-SE", { year: "numeric", month: "short" });
const dueLine = (f: Fixture): string => {
  const due = dueDate(f);
  const s = replacementStatus(f);
  if (!due || s === "none") return "";
  return s === "overdue" ? `Skulle bytts ${fmtMonth(due)}` : `Byt senast ${fmtMonth(due)}`;
};

const WALL_TOOL = "wall";
const BALCONY_TOOL = "balcony";
const TEXT_TOOL = "text";
const SELECT_TOOL = "select";
const SCALE_TOOL = "scale";
const MEASURE_TOOL = "measure";
const DOOR_TOOL = "door";
const WINDOW_TOOL = "window";

// Half-length (design units) of the 90° end ticks drawn across a measure line.
const TICK = 7;
// Rooms smaller than this (m²) are treated as noise and not shown/summed.
const MIN_ROOM_M2 = 1;

interface Snap { walls: Wall[]; windows: WindowOpening[]; fixtures: Fixture[]; labels: TextLabel[]; scale: number; }
type Clip = { kind: "fixture"; data: Fixture } | { kind: "wall"; data: Wall };

// Font size that keeps `text` inside a w×h box (shrinks for small fixtures).
const fitFont = (text: string, w: number, h: number, max = 15): number =>
  Math.max(4, Math.min(max, (w * 0.86) / (Math.max(1, text.length) * 0.62), h * 0.7));

// Line-art symbol for a fixture, drawn in the local 0..w / 0..h box — plan style.
const FixtureSymbol = ({ f }: { f: Fixture }) => {
  const { w, h } = f;
  const cx = w / 2, cy = h / 2;
  const m = Math.min(w, h);
  if (!DRAWN_KINDS.has(f.kind)) {
    const label = abbrForFixture(f);
    return <text className="hem__fix-abbr" style={{ fontSize: fitFont(label, w, h) }} x={cx} y={cy} textAnchor="middle" dominantBaseline="central">{label}</text>;
  }
  switch (f.kind) {
    case "spis":
      return <>{[0.32, 0.68].map((ry) => [0.32, 0.68].map((rx) => (
        <circle key={`${rx}-${ry}`} className="hem__fix-line" cx={w * rx} cy={h * ry} r={m * 0.13} />
      )))}</>;
    case "wc":
      return <>
        <rect className="hem__fix-line" x={w * 0.28} y={h * 0.05} width={w * 0.44} height={h * 0.22} />
        <ellipse className="hem__fix-line" cx={cx} cy={h * 0.62} rx={w * 0.3} ry={h * 0.3} />
      </>;
    case "handfat":
      return <>
        <rect className="hem__fix-line" x={w * 0.14} y={h * 0.14} width={w * 0.72} height={h * 0.72} rx={3} />
        <circle className="hem__fix-line" cx={cx} cy={cy} r={m * 0.12} />
      </>;
    case "dusch":
      return <>
        <line className="hem__fix-line" x1={2} y1={2} x2={w - 2} y2={h - 2} />
        <line className="hem__fix-line" x1={w - 2} y1={2} x2={2} y2={h - 2} />
        <circle className="hem__fix-line" cx={cx} cy={cy} r={2.5} />
      </>;
    case "badkar":
      return <>
        <rect className="hem__fix-line" x={w * 0.1} y={h * 0.18} width={w * 0.8} height={h * 0.64} rx={h * 0.3} />
        <circle className="hem__fix-line" cx={w * 0.82} cy={cy} r={2.5} />
      </>;
    case "element":
      return <>{Array.from({ length: 7 }, (_, i) => (
        <line key={i} className="hem__fix-line" x1={(w * (i + 1)) / 8} y1={2} x2={(w * (i + 1)) / 8} y2={h - 2} />
      ))}</>;
    case "brandvarnare":
      return <>
        <circle className="hem__fix-line" cx={cx} cy={cy} r={m * 0.34} />
        <circle className="hem__fix-dot" cx={cx} cy={cy} r={1.6} />
      </>;
    case "ugnmicro":
      // tall unit: microwave on top, oven below
      return <>
        <line className="hem__fix-line" x1={2} y1={h * 0.4} x2={w - 2} y2={h * 0.4} />
        <text className="hem__fix-abbr" style={{ fontSize: fitFont("MIC", w, h * 0.4) }} x={cx} y={h * 0.21} textAnchor="middle" dominantBaseline="central">MIC</text>
        <text className="hem__fix-abbr" style={{ fontSize: fitFont("UGN", w, h * 0.6) }} x={cx} y={h * 0.7} textAnchor="middle" dominantBaseline="central">UGN</text>
      </>;
    case "diskbank":
      // counter (the default box) with a sink basin, drain and a tap
      return <>
        <rect className="hem__fix-line" x={w * 0.1} y={h * 0.22} width={w * 0.38} height={h * 0.56} rx={3} />
        <circle className="hem__fix-line" cx={w * 0.29} cy={cy} r={m * 0.14} />
        <circle className="hem__fix-dot" cx={w * 0.29} cy={cy} r={1.6} />
        <line className="hem__fix-line" x1={w * 0.72} y1={h * 0.2} x2={w * 0.72} y2={h * 0.34} />
        <circle className="hem__fix-line" cx={w * 0.72} cy={h * 0.2} r={2.5} />
      </>;
    case "bankskiva":
      // see-through worktop: outline only, no text so what's under stays readable
      return null;
    case "hornsoffa": {
      const t = m * 0.5; // arm thickness
      const pts = `0,0 ${t},0 ${t},${h - t} ${w},${h - t} ${w},${h} 0,${h}`;
      return <>
        <polygon className="hem__fix-lbox" points={pts} />
        <line className="hem__fix-line" x1={t * 0.28} y1={5} x2={t * 0.28} y2={h - 5} />
        <line className="hem__fix-line" x1={5} y1={h - t * 0.28} x2={w - 5} y2={h - t * 0.28} />
      </>;
    }
    default:
      return null;
  }
};

// A doorway. Closed: the leaf lies in the opening with jamb ticks. Open: the
// opening span, the leaf swung 90° and its swing arc.
const DoorSymbol = ({ w, closed }: { w: Wall; closed?: boolean }) => {
  const dx = w.x2 - w.x1, dy = w.y2 - w.y1;
  const len = Math.hypot(dx, dy) || 1;
  const nx = -dy / len, ny = dx / len; // unit perpendicular
  if (closed) {
    const t = 5; // jamb tick half-length
    return (
      <g className="hem__door">
        <line className="hem__door-slab" x1={w.x1} y1={w.y1} x2={w.x2} y2={w.y2} />
        <line className="hem__door-jamb" x1={w.x1 - nx * t} y1={w.y1 - ny * t} x2={w.x1 + nx * t} y2={w.y1 + ny * t} />
        <line className="hem__door-jamb" x1={w.x2 - nx * t} y1={w.y2 - ny * t} x2={w.x2 + nx * t} y2={w.y2 + ny * t} />
      </g>
    );
  }
  const leafX = w.x1 + nx * len, leafY = w.y1 + ny * len; // hinge at (x1,y1), leaf perpendicular
  const a0 = Math.atan2(w.y2 - w.y1, w.x2 - w.x1);
  const a1 = Math.atan2(leafY - w.y1, leafX - w.x1);
  let da = a1 - a0;
  while (da > Math.PI) da -= 2 * Math.PI;
  while (da < -Math.PI) da += 2 * Math.PI;
  const arc = Array.from({ length: 13 }, (_, i) => {
    const a = a0 + (da * i) / 12;
    return `${(w.x1 + len * Math.cos(a)).toFixed(1)},${(w.y1 + len * Math.sin(a)).toFixed(1)}`;
  }).join(" ");
  return (
    <g className="hem__door">
      <line className="hem__door-gap" x1={w.x1} y1={w.y1} x2={w.x2} y2={w.y2} />
      <polyline className="hem__door-arc" points={arc} />
      <line className="hem__door-leaf" x1={w.x1} y1={w.y1} x2={leafX} y2={leafY} />
    </g>
  );
};

export const HemView = ({ uid, email }: { uid: string; email: string }) => {
  usePageBackground();
  const [homesList, setHomesList] = useState<{ owner: string; label: string; email?: string; mine: boolean }[]>([{ owner: uid, label: "Mitt hem", mine: true }]);
  const [activeOwner, setActiveOwner] = useState(uid);
  const [ownEmpty, setOwnEmpty] = useState(false);
  const [loadError, setLoadError] = useState("");
  const pickedHome = useRef(false);
  const [sharedWith, setSharedWith] = useState<string[]>([]);
  const [shareInput, setShareInput] = useState("");
  const [shareMsg, setShareMsg] = useState("");
  const [shareOpen, setShareOpen] = useState(false);
  const isOwner = activeOwner === uid;
  const [planUrl, setPlanUrl] = useState("");
  const [planRot, setPlanRot] = useState(0);
  const [walls, setWalls] = useState<Wall[]>([]);
  const [windows, setWindows] = useState<WindowOpening[]>([]);
  const [wallColors, setWallColors] = useState<WallColors>({});
  // first click of the window tool: the point and the wall it sits on
  const [winStart, setWinStart] = useState<{ x: number; y: number; wallId: string } | null>(null);
  const [fixtures, setFixtures] = useState<Fixture[]>([]);
  const [labels, setLabels] = useState<TextLabel[]>([]);
  const [rooms, setRooms] = useState<Room[]>([]);
  const [moodOpen, setMoodOpen] = useState(false);
  const [view3d, setView3dState] = useState<View3D>(() => {
    try { const v = localStorage.getItem("hem-3d"); return v === "split" || v === "full" ? v : "off"; } catch { return "off"; }
  });
  const setView3d = (v: View3D) => { setView3dState(v); try { localStorage.setItem("hem-3d", v); } catch { /* ignore */ } };
  const show3d = view3d === "full"; // 3D only: the 2D canvas is hidden
  const [scale, setScale] = useState(0); // design units per metre (0 = uncalibrated)
  const [scaleStart, setScaleStart] = useState<{ x: number; y: number } | null>(null);
  const [measureStart, setMeasureStart] = useState<{ x: number; y: number } | null>(null);
  const [doorStart, setDoorStart] = useState<{ x: number; y: number } | null>(null);
  const [measures, setMeasures] = useState<Measure[]>([]);
  const [actualArea, setActualArea] = useState(0); // real total floor area (m²), user-entered
  const [kvmInput, setKvmInput] = useState("");
  const [scaleInput, setScaleInput] = useState("");
  const [hydrated, setHydrated] = useState(false);
  const [editMode, setEditMode] = useState(() => localStorage.getItem("hem-edit") === "1");
  const [furnitureOnly, setFurnitureOnly] = useState(() => localStorage.getItem("hem-furn") === "1");
  const [tool, setTool] = useState<string>(SELECT_TOOL);
  const [wallStart, setWallStart] = useState<{ x: number; y: number } | null>(null);
  const [cursor, setCursor] = useState<{ x: number; y: number } | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [selectedWallId, setSelectedWallId] = useState<string | null>(null);
  const [editing, setEditing] = useState<Fixture | null>(null);
  const [viewing, setViewing] = useState<Fixture | null>(null); // read-only info (view mode)
  const [uploadMsg, setUploadMsg] = useState("");
  const [showPlan, setShowPlan] = useState(true);
  const [showMeasures, setShowMeasures] = useState(true);
  const [showAreas, setShowAreas] = useState(() => localStorage.getItem("hem-areas") !== "0");
  const [doorsClosed, setDoorsClosed] = useState(() => localStorage.getItem("hem-doors-closed") !== "0");
  const [view, setView] = useState({ x: 0, y: 0, w: PLAN_W, h: PLAN_H }); // viewBox (zoom/pan)

  const svgRef = useRef<SVGSVGElement>(null);
  const wrapRef = useRef<HTMLDivElement>(null);
  const [planH, setPlanH] = useState<number | null>(null); // JS-fitted plan height (px)
  const drag = useRef<{ id: string; dx: number; dy: number; moved: boolean } | null>(null);
  const pan = useRef<{ cx: number; cy: number; vx: number; vy: number } | null>(null);
  // undo history + clipboard
  const past = useRef<Snap[]>([]);
  const future = useRef<Snap[]>([]);
  const lastSnap = useRef<string>("");
  const skipHistory = useRef(false);
  const clipboard = useRef<Clip | null>(null);
  // dragging a whole wall ("move") or one of its endpoints ("a"/"b")
  const wallDrag = useRef<{ id: string; part: "move" | "a" | "b"; ox: number; oy: number; start: Wall } | null>(null);
  // manual double-tap detection: setPointerCapture below suppresses native dblclick
  const lastFixTap = useRef<{ id: string; t: number }>({ id: "", t: 0 });

  const setEdit = (on: boolean) => { setEditMode(on); localStorage.setItem("hem-edit", on ? "1" : "0"); };
  const setFurn = (on: boolean) => { setFurnitureOnly(on); localStorage.setItem("hem-furn", on ? "1" : "0"); };
  const setAreas = (on: boolean) => { setShowAreas(on); localStorage.setItem("hem-areas", on ? "1" : "0"); };
  const setDoorsClosedP = (on: boolean) => { setDoorsClosed(on); localStorage.setItem("hem-doors-closed", on ? "1" : "0"); };
  const enterEdit = (furn: boolean) => { setFurn(furn); setEdit(true); setTool(SELECT_TOOL); setSelectedWallId(null); };
  const toggleEdit = () => setEdit(!editMode);

  const normEmail = (s: string) => s.trim().toLowerCase();
  const addShare = () => {
    const e = normEmail(shareInput);
    if (!/.+@.+\..+/.test(e)) { setShareMsg("Ogiltig e-postadress."); return; }
    if (e === normEmail(email)) { setShareMsg("Det är din egen adress."); return; }
    if (sharedWith.includes(e)) { setShareMsg("Redan delad med den adressen."); return; }
    setSharedWith((p) => [...p, e]); setShareInput(""); setShareMsg(`Delad! ${e} kan nu öppna hemmet (hen måste logga in med den adressen).`);
  };
  const removeShare = (e: string) => { setSharedWith((p) => p.filter((x) => x !== e)); setShareMsg(""); };

  // List homes shared with me (own home is always first).
  useEffect(() => {
    const own: { owner: string; label: string; email?: string; mine: boolean } = { owner: uid, label: "Mitt hem", mine: true };
    if (!email) { setHomesList([own]); return; }
    db.collection("homes").where("sharedWith", "array-contains", email.toLowerCase()).get()
      .then((snap) => {
        const list = [own];
        snap.docs.forEach((d) => { if (d.id !== uid) list.push({ owner: d.id, label: "Delat hem", email: (d.data() as any)?.ownerEmail || "", mine: false }); });
        if (list.length > 2) list.forEach((x) => { if (!x.mine && x.email) x.label = x.email; });
        setHomesList(list);
      })
      .catch(() => setHomesList([own]));
  }, [uid, email]);

  // A collaborator with nothing of their own lands on the shared home instead of a blank canvas.
  useEffect(() => {
    if (ownEmpty && !pickedHome.current && activeOwner === uid && homesList.length > 1) setActiveOwner(homesList[1].owner);
  }, [ownEmpty, homesList, activeOwner, uid]);

  // Load the active home (own or a shared one); migrate own from the old
  // users/{uid}.home location on first load.
  useEffect(() => {
    let cancelled = false;
    setHydrated(false);
    setLoadError("");
    past.current = []; future.current = []; lastSnap.current = "";
    const hasContent = (d: any): boolean => !!(d && (d.planUrl || (d.walls && d.walls.length) || (d.fixtures && d.fixtures.length) || (d.labels && d.labels.length)));
    db.collection("homes").doc(activeOwner).get().then(async (doc) => {
      if (cancelled) return;
      let data: any = doc.exists ? doc.data() : null;
      // Recover/migrate from the old users/{uid}.home location if the homes doc
      // is missing or empty and hasn't been migrated yet.
      if (activeOwner === uid && !hasContent(data) && !data?.migrated) {
        const legacy = await db.collection("users").doc(uid).get();
        if (!cancelled) {
          const lh = legacy.data()?.home;
          if (hasContent(lh)) data = { ...lh, sharedWith: data?.sharedWith ?? [] };
        }
      }
      if (activeOwner === uid) setOwnEmpty(!hasContent(data));
      const home = normalizeHome(data);
      setPlanUrl(home.planUrl);
      setPlanRot(home.planRot);
      setShowPlan(!home.planHidden);
      setWalls(home.walls);
      setWindows(home.windows);
      setWallColors(home.wallColors);
      setFixtures(home.fixtures);
      setLabels(home.labels);
      setScale(home.scale);
      setMeasures(home.measures);
      setShowMeasures(!home.measuresHidden);
      setActualArea(home.actualArea);
      setRooms(home.rooms);
      setSharedWith(Array.isArray(data?.sharedWith) ? data.sharedWith : []);
      setHydrated(true);
    }).catch(() => { if (!cancelled) { setLoadError("Kunde inte öppna hemmet (saknar behörighet?). Kontrollera att du är inloggad med den e-postadress som hemmet delats med."); } });
    return () => { cancelled = true; };
  }, [activeOwner, uid]);

  useEffect(() => {
    if (!hydrated) return;
    const t = window.setTimeout(() => {
      const payload: any = { uid: activeOwner, sharedWith, planUrl, planRot, planHidden: !showPlan, walls, windows, wallColors, fixtures, labels, scale, measures, measuresHidden: !showMeasures, actualArea, rooms };
      if (activeOwner === uid) {
        payload.ownerEmail = (email || "").toLowerCase();
        // mark migrated once there's real content, so a blank home isn't re-restored
        if (planUrl || walls.length || fixtures.length || labels.length) payload.migrated = true;
      }
      db.collection("homes").doc(activeOwner).set(payload, { merge: true }).catch(() => {});
    }, 700);
    return () => window.clearTimeout(t);
  }, [planUrl, planRot, showPlan, walls, windows, wallColors, fixtures, labels, scale, measures, showMeasures, actualArea, rooms, sharedWith, hydrated, activeOwner, uid, email]);

  // Record undo snapshots, debounced so a whole drag coalesces into one step.
  useEffect(() => {
    if (!hydrated) return;
    const json = JSON.stringify({ walls, windows, fixtures, labels, scale });
    if (skipHistory.current) { skipHistory.current = false; lastSnap.current = json; return; }
    if (lastSnap.current === "") { lastSnap.current = json; return; }
    if (lastSnap.current === json) return;
    const prev = lastSnap.current;
    const t = window.setTimeout(() => {
      past.current.push(JSON.parse(prev));
      if (past.current.length > 60) past.current.shift();
      future.current = [];
      lastSnap.current = json;
    }, 350);
    return () => window.clearTimeout(t);
  }, [walls, windows, fixtures, labels, scale, hydrated]);

  // Fit the whole (portrait) plan within the viewport: cap width so the
  // aspect-derived height never exceeds the space below the canvas top.
  useEffect(() => {
    const calc = () => {
      const wrap = wrapRef.current;
      if (!wrap) return;
      // Measure from the plan itself so the tool bar above it is accounted for
      const top = (svgRef.current ?? wrap).getBoundingClientRect().top;
      const availH = window.innerHeight - top - 16;
      const availW = wrap.clientWidth;
      // Height that fits vertically, but no taller than a full-width plan would be.
      setPlanH(Math.max(240, Math.min(availH, (availW * PLAN_H) / PLAN_W)));
    };
    calc();
    window.addEventListener("resize", calc);
    const id = window.setTimeout(calc, 100); // after layout settles
    return () => { window.removeEventListener("resize", calc); window.clearTimeout(id); };
  }, [editMode, hydrated, view3d]);

  // Keyboard: Esc leaves the current drawing tool; Delete removes the selected
  // fixture or wall; R rotates the selected fixture (unless typing in a field).
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (!editMode) return;
      const tag = (e.target as HTMLElement)?.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA") return;
      if (e.key === "Escape") {
        e.preventDefault();
        setTool(SELECT_TOOL);
        setWallStart(null);
        setScaleStart(null);
        setMeasureStart(null);
        setDoorStart(null);
        setWinStart(null);
        setSelectedId(null);
        setSelectedWallId(null);
        return;
      }
      if (e.key === "Delete" || e.key === "Backspace") {
        if (selectedId) {
          e.preventDefault();
          setFixtures((cur) => cur.filter((f) => f.id !== selectedId));
          setSelectedId(null);
        } else if (selectedWallId) {
          e.preventDefault();
          setWalls((cur) => cur.filter((w) => w.id !== selectedWallId));
          setSelectedWallId(null);
        }
      }
      if ((e.key === "r" || e.key === "R") && selectedId) {
        e.preventDefault();
        setFixtures((cur) => cur.map((f) => (f.id === selectedId ? { ...f, rot: (((f.rot || 0) + 90) % 360) } : f)));
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [editMode, selectedId, selectedWallId, tool]);

  const selected = useMemo(() => fixtures.find((f) => f.id === selectedId) || null, [fixtures, selectedId]);
  // Enclosed rooms found in the walls+doors network (for the per-room m² labels).
  const roomAreas = useMemo(() => detectRooms(walls), [walls]);
  const hasDoors = useMemo(() => walls.some((w) => w.door), [walls]);
  // Open wall ends — gaps that stop a room from closing (shown while editing).
  const openEnds = useMemo(() => openWallEnds(walls), [walls]);
  // Drawn wall geometry: connected ends extended so corners meet cleanly.
  const wallDraw = useMemo(() => extendedWalls(walls), [walls]);
  // Estimated floor area, split into living (excl. balcony) and balcony (m²).
  const { estLiving, estBalcony } = useMemo(() => {
    let living = 0, balc = 0;
    if (scale > 0) for (const r of roomAreas) {
      const a = r.areaUnits / (scale * scale);
      if (a < MIN_ROOM_M2) continue;
      if (r.balcony) balc += a; else living += a;
    }
    return { estLiving: living, estBalcony: balc };
  }, [roomAreas, scale]);
  const estInclBalcony = estLiving + estBalcony;
  const fmtM2 = (v: number) => `${v.toFixed(1).replace(".", ",")} m²`;
  const editActualArea = () => {
    const next = window.prompt("Faktisk boarea (m²) — t.ex. 61. Lämna tomt för att ta bort:", actualArea > 0 ? String(actualArea).replace(".", ",") : "");
    if (next === null) return;
    const v = parseFloat(next.replace(",", "."));
    setActualArea(isFinite(v) && v > 0 ? +v.toFixed(1) : 0);
  };

  // client → design coords
  const toUserXY = (clientX: number, clientY: number): { x: number; y: number } => {
    const svg = svgRef.current;
    const ctm = svg?.getScreenCTM();
    if (!svg || !ctm) return { x: 0, y: 0 };
    const p = svg.createSVGPoint();
    p.x = clientX; p.y = clientY;
    const u = p.matrixTransform(ctm.inverse());
    return { x: u.x, y: u.y };
  };
  const toUser = (e: React.PointerEvent): { x: number; y: number } => toUserXY(e.clientX, e.clientY);

  // Snap (x,y) to another wall so lines actually connect: a wall endpoint first
  // (priority), else the nearest point on a wall body (a T-junction). `excludeId`
  // skips the wall being dragged so it can't snap onto itself.
  const snapToWalls = (x: number, y: number, excludeId?: string, tolEnd = 14, tolSeg = 10): { x: number; y: number } | null => {
    let best: { x: number; y: number } | null = null;
    let bd = tolEnd;
    for (const w of walls) {
      if (w.id === excludeId) continue;
      for (const p of [{ x: w.x1, y: w.y1 }, { x: w.x2, y: w.y2 }]) {
        const d = Math.hypot(p.x - x, p.y - y);
        if (d <= bd) { bd = d; best = p; }
      }
    }
    if (best) return best;
    let sb = tolSeg;
    for (const w of walls) {
      if (w.id === excludeId) continue;
      const dx = w.x2 - w.x1, dy = w.y2 - w.y1, L2 = dx * dx + dy * dy || 1;
      let t = ((x - w.x1) * dx + (y - w.y1) * dy) / L2;
      t = Math.max(0, Math.min(1, t));
      const projx = w.x1 + t * dx, projy = w.y1 + t * dy;
      const d = Math.hypot(projx - x, projy - y);
      if (d <= sb) { sb = d; best = { x: Math.round(projx), y: Math.round(projy) }; }
    }
    return best;
  };

  // Nearest point on an ordinary wall (windows only go in real walls). With
  // `onWallId`, project onto that wall's line instead (the window's 2nd end).
  const snapToWallBody = (x: number, y: number, onWallId?: string): { x: number; y: number; wallId: string } | null => {
    let best: { x: number; y: number; wallId: string } | null = null;
    let bd = onWallId ? Infinity : 16;
    for (const w of walls) {
      if (w.door || w.balcony || (onWallId && w.id !== onWallId)) continue;
      const dx = w.x2 - w.x1, dy = w.y2 - w.y1, L2 = dx * dx + dy * dy || 1;
      const t = Math.max(0, Math.min(1, ((x - w.x1) * dx + (y - w.y1) * dy) / L2));
      const px = w.x1 + t * dx, py = w.y1 + t * dy;
      const d = Math.hypot(px - x, py - y);
      if (d <= bd) { bd = d; best = { x: px, y: py, wallId: w.id }; }
    }
    return best;
  };

  // distance label: metres when calibrated, otherwise raw units
  const mLabel = (dist: number): string => (scale > 0 ? `${(dist / scale).toFixed(2).replace(".", ",")} m` : `${Math.round(dist)} px`);
  // what a saved measure shows: the hand-entered value if any, else the computed length
  const measureText = (m: Measure): string => (m.label && m.label.trim() ? m.label.trim() : mLabel(Math.hypot(m.x2 - m.x1, m.y2 - m.y1)));

  // Endpoints of a 90° tick (half-length TICK) centred on (px,py), perpendicular
  // to the measure line (x1,y1)->(x2,y2) — classic dimension-line end marks.
  const tickEnds = (x1: number, y1: number, x2: number, y2: number, px: number, py: number) => {
    const len = Math.hypot(x2 - x1, y2 - y1) || 1;
    const nx = -(y2 - y1) / len, ny = (x2 - x1) / len; // unit perpendicular
    return { x1: px - nx * TICK, y1: py - ny * TICK, x2: px + nx * TICK, y2: py + ny * TICK };
  };

  // Enter/clear the real-world value for a saved measure (empty reverts to the scale).
  const editMeasure = (m: Measure) => {
    const next = window.prompt("Riktigt mått (t.ex. 2,35 m). Lämna tomt för att använda skalan:", m.label ?? "");
    if (next === null) return; // cancelled
    const v = next.trim();
    setMeasures((cur) => cur.map((x) => {
      if (x.id !== m.id) return x;
      if (v) return { ...x, label: v };
      const { label, ...rest } = x; // drop the key entirely (no undefined for Firestore)
      return rest;
    }));
  };

  // ── zoom / pan (viewBox) ──
  const clamp = (n: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, n));
  const ASPECT = PLAN_H / PLAN_W;
  const zoomAt = useCallback((clientX: number, clientY: number, factor: number) => {
    const rect = svgRef.current?.getBoundingClientRect();
    if (!rect) return;
    setView((v) => {
      const w = clamp(v.w * factor, PLAN_W / 8, PLAN_W);
      const h = w * ASPECT;
      const fx = (clientX - rect.left) / rect.width;   // 0..1 across the canvas
      const fy = (clientY - rect.top) / rect.height;
      const ux = v.x + fx * v.w;                        // design point under cursor
      const uy = v.y + fy * v.h;
      const x = clamp(ux - fx * w, 0, PLAN_W - w);
      const y = clamp(uy - fy * h, 0, PLAN_H - h);
      return { x, y, w, h };
    });
  }, [ASPECT]);
  const zoomCenter = (factor: number) => {
    const rect = svgRef.current?.getBoundingClientRect();
    if (!rect) return;
    zoomAt(rect.left + rect.width / 2, rect.top + rect.height / 2, factor);
  };
  const resetView = () => setView({ x: 0, y: 0, w: PLAN_W, h: PLAN_H });
  const zoomPct = Math.round((PLAN_W / view.w) * 100);

  // wheel-zoom centred on the cursor (native listener so we can preventDefault)
  useEffect(() => {
    const svg = svgRef.current;
    if (!svg) return;
    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      zoomAt(e.clientX, e.clientY, e.deltaY < 0 ? 0.88 : 1.14);
    };
    svg.addEventListener("wheel", onWheel, { passive: false });
    return () => svg.removeEventListener("wheel", onWheel);
  }, [zoomAt]);

  // Approximate scale from a typed floor area (m²): assume the walls' bounding
  // box ≈ the apartment footprint (fills ~80% of it). scale² = area_units / m².
  const scaleFromArea = () => {
    const kvm = parseFloat(kvmInput.replace(",", "."));
    if (!kvm || kvm <= 0) return;
    const areaWalls = walls.filter((w) => !w.balcony); // balconies don't count toward m²
    if (areaWalls.length < 2) { setUploadMsg("Rita väggarna först — eller använd 📏 Skala."); return; }
    const xs = areaWalls.flatMap((w) => [w.x1, w.x2]);
    const ys = areaWalls.flatMap((w) => [w.y1, w.y2]);
    const bw = Math.max(...xs) - Math.min(...xs);
    const bh = Math.max(...ys) - Math.min(...ys);
    const areaUnits = bw * bh * 0.8;
    if (areaUnits <= 0) return;
    setScale(+Math.sqrt(areaUnits / kvm).toFixed(2));
    setUploadMsg(`Skala satt utifrån ${kvm} m² (ungefärlig — finjustera med 📏 Skala vid behov).`);
  };

  const rotatePlan = (d: number) => setPlanRot((r) => {
    let n = (r + d) % 360;
    if (n > 180) n -= 360;
    if (n < -180) n += 360;
    return +n.toFixed(1);
  });

  // Set the scale directly (design units per metre), e.g. a pre-computed value.
  const applyScaleInput = () => {
    const v = parseFloat(scaleInput.replace(",", "."));
    if (!v || v <= 0) return;
    setScale(+v.toFixed(2));
    setScaleInput("");
    setUploadMsg(`Skala satt till ${v} px/m.`);
  };

  // Drag a palette item and drop it on the plan.
  const onCanvasDrop = (e: React.DragEvent<SVGSVGElement>) => {
    e.preventDefault();
    const kind = e.dataTransfer.getData("hem-kind");
    if (!kind) return;
    const u = toUserXY(e.clientX, e.clientY);
    const f = blankFixture(kind, u.x, u.y, scale);
    setFixtures((cur) => [...cur, f]);
    setSelectedId(f.id);
    setTool(SELECT_TOOL);
  };

  const updateFixture = (id: string, patch: Partial<Fixture>) => setFixtures((cur) => cur.map((f) => (f.id === id ? { ...f, ...patch } : f)));
  const deleteFixture = (id: string) => { setFixtures((cur) => cur.filter((f) => f.id !== id)); setEditing(null); setSelectedId(null); };

  // ── undo / copy-paste ──
  const applySnap = (s: Snap) => {
    skipHistory.current = true;
    setWalls(s.walls); setWindows(s.windows ?? []); setFixtures(s.fixtures); setLabels(s.labels); setScale(s.scale);
    setSelectedId(null); setSelectedWallId(null);
    lastSnap.current = JSON.stringify(s);
  };
  const undo = () => {
    if (past.current.length === 0) return;
    future.current.push({ walls, windows, fixtures, labels, scale });
    applySnap(past.current.pop()!);
  };
  const redo = () => {
    if (future.current.length === 0) return;
    past.current.push({ walls, windows, fixtures, labels, scale });
    applySnap(future.current.pop()!);
  };
  const copySel = () => {
    if (selectedId) { const f = fixtures.find((x) => x.id === selectedId); if (f) clipboard.current = { kind: "fixture", data: f }; }
    else if (selectedWallId) { const w = walls.find((x) => x.id === selectedWallId); if (w) clipboard.current = { kind: "wall", data: w }; }
  };
  const paste = () => {
    const c = clipboard.current;
    if (!c) return;
    if (c.kind === "fixture") {
      const nf: Fixture = { ...c.data, id: newId(), x: snap(c.data.x + 15), y: snap(c.data.y + 15) };
      setFixtures((cur) => [...cur, nf]); setSelectedId(nf.id); setSelectedWallId(null);
    } else {
      const w = c.data;
      const nw: Wall = { ...w, id: newId(), x1: w.x1 + 15, y1: w.y1 + 15, x2: w.x2 + 15, y2: w.y2 + 15 };
      setWalls((cur) => [...cur, nw]); setSelectedWallId(nw.id); setSelectedId(null);
    }
  };
  const actions = useRef({ undo, redo, copySel, paste, editMode });
  actions.current = { undo, redo, copySel, paste, editMode };

  // Cmd/Ctrl shortcuts: Z undo, Shift+Z / Y redo, C copy, V paste
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const a = actions.current;
      if (!a.editMode || !(e.metaKey || e.ctrlKey)) return;
      const el = e.target as HTMLElement;
      const tag = el?.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || el?.isContentEditable) return;
      const k = e.key.toLowerCase();
      if (k === "z") { e.preventDefault(); e.shiftKey ? a.redo() : a.undo(); }
      else if (k === "y") { e.preventDefault(); a.redo(); }
      else if (k === "c") { e.preventDefault(); a.copySel(); }
      else if (k === "v") { e.preventDefault(); a.paste(); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const onPickFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setUploadMsg("Bearbetar bild…");
    try {
      let url = await fileToDataUrl(file, 1400, 0.82);
      if (url.length > 900000) url = await fileToDataUrl(file, 1000, 0.7);
      if (url.length > 900000) { setUploadMsg("Bilden är för stor — prova en mindre fil."); return; }
      setPlanUrl(url); setUploadMsg("");
    } catch { setUploadMsg("Kunde inte läsa bilden."); }
  };

  // ── canvas pointer handling ──
  const onCanvasDown = (e: React.PointerEvent<SVGSVGElement>) => {
    // Measuring works in both edit and view mode.
    if (tool === MEASURE_TOOL) {
      const mu = toUser(e);
      const p = { x: Math.round(mu.x), y: Math.round(mu.y) };
      if (!measureStart) setMeasureStart(p);
      else { setMeasures((cur) => [...cur, { id: newId(), x1: measureStart.x, y1: measureStart.y, x2: p.x, y2: p.y }]); setMeasureStart(null); }
      return;
    }
    // Pressing empty canvas in select/view mode starts a pan (fixtures & walls
    // stopPropagation, so this only fires on the background).
    if (!editMode || tool === SELECT_TOOL) {
      pan.current = { cx: e.clientX, cy: e.clientY, vx: view.x, vy: view.y };
      svgRef.current?.setPointerCapture(e.pointerId);
    }
    if (!editMode) return;
    const u = toUser(e);
    if (tool === SCALE_TOOL) {
      if (!scaleStart) setScaleStart({ x: u.x, y: u.y });
      else {
        const dist = Math.hypot(u.x - scaleStart.x, u.y - scaleStart.y);
        const meters = parseFloat((window.prompt("Hur många meter är sträckan du just klickade ut?", "") || "").replace(",", "."));
        if (meters && meters > 0 && dist > 0) setScale(+(dist / meters).toFixed(2));
        setScaleStart(null);
        setTool(SELECT_TOOL);
      }
      return;
    }
    if (tool === WALL_TOOL || tool === BALCONY_TOOL) {
      // Shift = free placement (no wall-snap, no grid-snap).
      const p = e.shiftKey ? { x: Math.round(u.x), y: Math.round(u.y) } : (snapToWalls(u.x, u.y) ?? { x: snap(u.x), y: snap(u.y) });
      if (!wallStart) setWallStart(p);
      else { setWalls((cur) => [...cur, { id: newId(), x1: wallStart.x, y1: wallStart.y, x2: p.x, y2: p.y, balcony: tool === BALCONY_TOOL }]); setWallStart(p); }
      return;
    }
    if (tool === DOOR_TOOL) {
      const near = e.shiftKey ? null : snapToWalls(u.x, u.y, undefined, 18, 12);
      const p = near ?? { x: snap(u.x), y: snap(u.y) };
      if (!doorStart) setDoorStart(p);
      else {
        setWalls((cur) => [...cur, { id: newId(), x1: doorStart.x, y1: doorStart.y, x2: p.x, y2: p.y, door: true }]);
        setDoorStart(null);
      }
      return;
    }
    if (tool === WINDOW_TOOL) {
      if (!winStart) {
        const p = snapToWallBody(u.x, u.y);
        if (p) setWinStart(p);
      } else {
        const p = snapToWallBody(u.x, u.y, winStart.wallId);
        if (p && Math.hypot(p.x - winStart.x, p.y - winStart.y) > 4) {
          setWindows((cur) => [...cur, { id: newId(), x1: winStart.x, y1: winStart.y, x2: p.x, y2: p.y }]);
        }
        setWinStart(null);
      }
      return;
    }
    if (tool === TEXT_TOOL) {
      const text = window.prompt("Text (t.ex. rummets namn):", "");
      if (text && text.trim()) setLabels((cur) => [...cur, { id: newId(), text: text.trim(), x: snap(u.x), y: snap(u.y) }]);
      return;
    }
    if (tool.startsWith("fix:")) {
      const f = blankFixture(tool.slice(4), u.x, u.y, scale);
      setFixtures((cur) => [...cur, f]);
      setSelectedId(f.id);
      return;
    }
    // select tool: clicking empty canvas clears selection
    setSelectedId(null);
    setSelectedWallId(null);
  };

  const onCanvasMove = (e: React.PointerEvent<SVGSVGElement>) => {
    if (pan.current) {
      const rect = svgRef.current?.getBoundingClientRect();
      if (rect) {
        const s = view.w / rect.width;
        const p = pan.current;
        setView((v) => ({
          ...v,
          x: clamp(p.vx - (e.clientX - p.cx) * s, 0, PLAN_W - v.w),
          y: clamp(p.vy - (e.clientY - p.cy) * s, 0, PLAN_H - v.h),
        }));
      }
      return;
    }
    // measure preview works in view mode too
    if (tool === MEASURE_TOOL && measureStart) { setCursor(toUser(e)); return; }
    if (!editMode) return;
    const u = toUser(e);
    if ((tool === WALL_TOOL || tool === BALCONY_TOOL) && wallStart) setCursor(e.shiftKey ? { x: Math.round(u.x), y: Math.round(u.y) } : (snapToWalls(u.x, u.y) ?? { x: snap(u.x), y: snap(u.y) }));
    else if (tool === WINDOW_TOOL && winStart) { const p = snapToWallBody(u.x, u.y, winStart.wallId); if (p) setCursor(p); }
    else if (tool === DOOR_TOOL && doorStart) { const n = e.shiftKey ? null : snapToWalls(u.x, u.y, undefined, 18, 12); setCursor(n ?? { x: snap(u.x), y: snap(u.y) }); }
    else if (tool === SCALE_TOOL && scaleStart) setCursor(u);
    if (wallDrag.current) {
      const d = wallDrag.current;
      if (d.part === "move") {
        const dx = Math.round(u.x - d.ox);
        const dy = Math.round(u.y - d.oy);
        setWalls((cur) => cur.map((w) => (w.id === d.id
          ? { ...w, x1: d.start.x1 + dx, y1: d.start.y1 + dy, x2: d.start.x2 + dx, y2: d.start.y2 + dy }
          : w)));
      } else {
        const sp = e.shiftKey ? null : snapToWalls(u.x, u.y, d.id);
        const px = sp ? sp.x : Math.round(u.x), py = sp ? sp.y : Math.round(u.y);
        setWalls((cur) => cur.map((w) => {
          if (w.id !== d.id) return w;
          return d.part === "a" ? { ...w, x1: px, y1: py } : { ...w, x2: px, y2: py };
        }));
      }
      return;
    }
    if (drag.current) {
      const d = drag.current;
      // Track the pointer 1:1 (no GRID snap) so small moves nudge the item a
      // little instead of jumping a whole grid step.
      const nx = Math.round(u.x - d.dx);
      const ny = Math.round(u.y - d.dy);
      d.moved = true;
      updateFixture(d.id, { x: nx, y: ny });
    }
  };
  const onCanvasUp = () => {
    // A tap just selects; the editor opens on double-click (see the fixture group).
    drag.current = null;
    wallDrag.current = null;
    pan.current = null;
  };

  const onWallBodyDown = (e: React.PointerEvent, w: Wall) => {
    if (furnitureOnly) return;        // walls are locked in furniture mode
    if (tool !== SELECT_TOOL) return; // in wall tool let the click place points
    e.stopPropagation();
    setSelectedWallId(w.id); setSelectedId(null);
    const u = toUser(e);
    svgRef.current?.setPointerCapture(e.pointerId);
    wallDrag.current = { id: w.id, part: "move", ox: u.x, oy: u.y, start: { ...w } };
  };
  const onWallEndDown = (e: React.PointerEvent, w: Wall, part: "a" | "b") => {
    e.stopPropagation();
    setSelectedWallId(w.id); setSelectedId(null);
    svgRef.current?.setPointerCapture(e.pointerId);
    wallDrag.current = { id: w.id, part, ox: 0, oy: 0, start: { ...w } };
  };

  const onFixtureDown = (e: React.PointerEvent, f: Fixture) => {
    e.stopPropagation();
    if (!editMode) { setSelectedId(f.id); return; }
    // Double-tap opens the editor. setPointerCapture (below) eats the native
    // dblclick, so detect it manually from two quick pointerdowns.
    const now = Date.now();
    if (lastFixTap.current.id === f.id && now - lastFixTap.current.t < 350) {
      lastFixTap.current = { id: "", t: 0 };
      drag.current = null;
      setEditing(f);
      return;
    }
    lastFixTap.current = { id: f.id, t: now };
    const u = toUser(e);
    svgRef.current?.setPointerCapture(e.pointerId);
    drag.current = { id: f.id, dx: u.x - f.x, dy: u.y - f.y, moved: false };
    setSelectedId(f.id);
    setSelectedWallId(null);
  };

  // Read windows off the background floor-plan image (outer walls only)
  const findWindows = async () => {
    if (!planUrl) return;
    setUploadMsg("Letar efter fönster i ritningen…");
    try {
      const found = await detectWindows(planUrl, planRot, walls, windows, scale);
      setWindows((cur) => [...cur, ...found]);
      setUploadMsg(found.length
        ? `Lade till ${found.length} fönster. Kolla dem — klicka på ett fönster med 🪟 Fönster för att ta bort fel.`
        : "Hittade inga nya fönster. Ytterväggarna behöver bilda slutna rum och ligga ovanpå väggarna i bilden.");
    } catch (err) {
      setUploadMsg(err instanceof Error ? err.message : "Kunde inte läsa ritningen.");
    }
  };

  // ── callbacks from the 3D view ──
  const select3d = (id: string | null) => { setSelectedId(id); setSelectedWallId(null); };
  const moveFixture3d = (id: string, cx: number, cy: number) =>
    setFixtures((cur) => cur.map((f) => (f.id === id ? { ...f, x: Math.round(cx - f.w / 2), y: Math.round(cy - f.h / 2) } : f)));
  const rotateFixture3d = (id: string) =>
    setFixtures((cur) => cur.map((f) => (f.id === id ? { ...f, rot: ((f.rot || 0) + 90) % 360 } : f)));
  const paintFixture3d = (id: string, color: string | null, part: "main" | "top") =>
    setFixtures((cur) => cur.map((f) => {
      if (f.id !== id) return f;
      const key = part === "top" ? "topColor" : "color";
      const next = { ...f };
      if (color) next[key] = color;
      else delete next[key];
      return next;
    }));
  const paintWalls = (changes: PaintChange[], color: string | null) =>
    setWallColors((cur) => {
      const next: WallColors = { ...cur };
      changes.forEach(({ wallId, side, a, b }) => {
        next[wallId] = { ...(next[wallId] || {}), [side]: applyPaint(next[wallId]?.[side], a, b, color) };
      });
      return next;
    });

  return (
    <div className="hem">
      <div className="hem__header">
        <NavLink to={routeUrls.PROJEKT} className="hem__back">← Projekt</NavLink>
        <h1 className="hem__title">Hem</h1>
        <span className="hem__sub">Planritning — väggar, inredning &amp; vitvaror</span>
        <span className="hem__modes">
          <button className="hem__mode" onClick={() => setMoodOpen(true)}>🎨 Moodboards</button>
          <span className="hem__viewseg" role="group" aria-label="Vy">
            {([["off", "🗺️ 2D"], ["split", "◧ 2D+3D"], ["full", "🧊 3D"]] as [View3D, string][]).map(([v, label]) => (
              <button key={v} className={`hem__mode${view3d === v ? " hem__mode--on" : ""}`} aria-pressed={view3d === v} onClick={() => { setView3d(v); if (v === "full") setEdit(false); }}>{label}</button>
            ))}
          </span>
          {show3d ? null : editMode ? (
            <button className="hem__mode hem__mode--on" onClick={() => setEdit(false)}>✓ Klar</button>
          ) : (
            <>
              <button className="hem__mode" onClick={() => enterEdit(true)}>🛋️ Möblera</button>
              <button className="hem__mode" onClick={() => enterEdit(false)}>✏️ Redigera allt</button>
            </>
          )}
        </span>
      </div>

      <div className="hem__scroll">
      {loadError && <div className="hem__empty-hint">{loadError}</div>}
      {(homesList.length > 1 || isOwner) && (
        <div className="hem__sharebar">
          {homesList.length > 1 && (
            <span className="hem__homesel">
              {homesList.map((hh) => (
                <button key={hh.owner} title={hh.email || undefined} className={`hem__tool${activeOwner === hh.owner ? " hem__tool--on" : ""}`} onClick={() => { pickedHome.current = true; setActiveOwner(hh.owner); }}>
                  {hh.mine ? "🏠 " : "👥 "}{hh.label}
                </button>
              ))}
            </span>
          )}
          {isOwner && <button className="hem__tool" onClick={() => { setShareOpen(true); setShareMsg(""); }}>👥 Dela{sharedWith.length ? ` (${sharedWith.length})` : ""}</button>}
          {!isOwner && <span className="hem__empty-hint">Du redigerar ett delat hem.</span>}
        </div>
      )}
      <div className={`hem__work${view3d === "split" ? " hem__work--split" : ""}`}>
      {show3d && (
        <React.Suspense fallback={<p className="hem__empty-hint">Laddar 3D…</p>}>
          <Hem3D
            walls={walls} windows={windows} fixtures={fixtures} labels={labels} scale={scale}
            wallColors={wallColors} doorsClosed={doorsClosed} setDoorsClosed={setDoorsClosedP}
            selectedId={selectedId} onSelect={select3d} onMoveFixture={moveFixture3d}
            onRotateFixture={rotateFixture3d} onPaint={paintWalls} onPaintFixture={paintFixture3d}
          />
        </React.Suspense>
      )}
      {editMode && !show3d && (
        <div className="hem__toolbar">
          <div className="hem__tools">
            <button className={`hem__tool${furnitureOnly ? " hem__tool--on" : ""}`} onClick={() => { setFurn(true); setTool(SELECT_TOOL); setSelectedWallId(null); }} title="Flytta bara möbler — väggar låsta">🛋️ Möbler</button>
            <button className={`hem__tool${!furnitureOnly ? " hem__tool--on" : ""}`} onClick={() => setFurn(false)} title="Redigera väggar, text, skala och möbler">🏗️ Allt</button>
            <span className="hem__tool-sep" />
            <button className={`hem__tool${tool === SELECT_TOOL ? " hem__tool--on" : ""}`} onClick={() => { setTool(SELECT_TOOL); setWallStart(null); }}>🖱️ Välj</button>
            {!furnitureOnly && <button className={`hem__tool${tool === WALL_TOOL ? " hem__tool--on" : ""}`} onClick={() => { setTool(WALL_TOOL); setSelectedId(null); }}>🧱 Vägg</button>}
            {!furnitureOnly && <button className={`hem__tool${tool === BALCONY_TOOL ? " hem__tool--on" : ""}`} onClick={() => { setTool(BALCONY_TOOL); setSelectedId(null); }}>🌤️ Balkong</button>}
            {!furnitureOnly && <button className={`hem__tool${tool === DOOR_TOOL ? " hem__tool--on" : ""}`} onClick={() => { setTool(DOOR_TOOL); setDoorStart(null); setSelectedId(null); setSelectedWallId(null); }} title="Täpp till en dörröppning så rummet sluts och ytan räknas ut">🚪 Dörr</button>}
            {!furnitureOnly && <button className={`hem__tool${tool === WINDOW_TOOL ? " hem__tool--on" : ""}`} onClick={() => { setTool(WINDOW_TOOL); setWinStart(null); setSelectedId(null); setSelectedWallId(null); }} title="Sätt fönster i en vägg — syns i 3D-vyn">🪟 Fönster</button>}
            {!furnitureOnly && planUrl && <button className="hem__tool" onClick={findWindows} title="Läs fönstren från planritningsbilden längs ytterväggarna">🪄 Hitta fönster</button>}
            {!furnitureOnly && <button className={`hem__tool${tool === TEXT_TOOL ? " hem__tool--on" : ""}`} onClick={() => { setTool(TEXT_TOOL); setWallStart(null); }}>🏷️ Text</button>}
            {!furnitureOnly && <button className={`hem__tool${tool === SCALE_TOOL ? " hem__tool--on" : ""}`} onClick={() => { setTool(SCALE_TOOL); setScaleStart(null); setSelectedId(null); }}>📏 Skala</button>}
            <button className={`hem__tool${tool === MEASURE_TOOL ? " hem__tool--on" : ""}`} onClick={() => { setTool(MEASURE_TOOL); setMeasureStart(null); setSelectedId(null); setSelectedWallId(null); }}>📐 Mät</button>
            {(tool === WALL_TOOL || tool === BALCONY_TOOL) && wallStart && <button className="hem__btn" onClick={() => setWallStart(null)}>✓ Avsluta {tool === BALCONY_TOOL ? "balkong" : "vägg"}</button>}
            {measures.length > 0 && <button className="hem__btn" onClick={() => setShowMeasures((v) => !v)}>{showMeasures ? "🙈 Dölj mått" : "👁 Visa mått"}</button>}
            {measures.length > 0 && <button className="hem__btn" onClick={() => { setMeasures((cur) => cur.filter((m) => !!(m.label && m.label.trim()))); setMeasureStart(null); }} title="Rensar mått utan inskrivet värde">🧹 Rensa mått</button>}
            {roomAreas.length > 0 && <button className="hem__btn" onClick={() => setAreas(!showAreas)} title="Visa/dölj uträknad yta per rum">{showAreas ? "🙈 Dölj ytor" : "📐 Visa ytor"}</button>}
            {hasDoors && <button className="hem__btn" onClick={() => setDoorsClosedP(!doorsClosed)} title="Rita dörrarna öppna (svängbåge) eller stängda">{doorsClosed ? "🚪 Öppna dörrar" : "🚪 Stäng dörrar"}</button>}
            <span className="hem__tool-sep" />
            {!furnitureOnly && <label className="hem__tool">📤 {planUrl ? "Byt bild" : "Bakgrundsbild"}<input type="file" accept="image/*" hidden onChange={onPickFile} /></label>}
            {planUrl && <button className="hem__tool" onClick={() => setShowPlan((v) => !v)}>{showPlan ? "🙈 Dölj bild" : "🖼️ Visa bild"}</button>}
            {planUrl && !furnitureOnly && <button className="hem__link-btn hem__link-btn--danger" onClick={() => setPlanUrl("")}>Ta bort bild</button>}
            {planUrl && !furnitureOnly && (
              <span className="hem__rotctl" title="Rotera bakgrundsbilden">
                🔄
                <input className="hem__rot-slider" type="range" min={-180} max={180} step={0.5} value={planRot} onChange={(e) => setPlanRot(Number(e.target.value))} />
                <button className="hem__tool" onClick={() => rotatePlan(-1)} title="−1°">−1°</button>
                <button className="hem__tool" onClick={() => rotatePlan(1)} title="+1°">+1°</button>
                <button className="hem__tool" onClick={() => rotatePlan(90)} title="90°">90°</button>
                <span className="hem__scale-badge">{planRot}°</span>
                {planRot !== 0 && <button className="hem__tool" onClick={() => setPlanRot(0)}>↺</button>}
              </span>
            )}
            {!furnitureOnly && <span className="hem__tool-sep" />}
            {!furnitureOnly && (
              <span className="hem__kvm">
                <input className="hem__kvm-input" value={kvmInput} onChange={(e) => setKvmInput(e.target.value.replace(/[^0-9.,]/g, ""))} placeholder="m²" inputMode="decimal" onKeyDown={(e) => { if (e.key === "Enter") scaleFromArea(); }} />
                <button className="hem__tool" onClick={scaleFromArea} title="Sätt skala utifrån boarea">Skala via m²</button>
              </span>
            )}
            {!furnitureOnly && (
              <span className="hem__kvm">
                <input className="hem__kvm-input" value={scaleInput} onChange={(e) => setScaleInput(e.target.value.replace(/[^0-9.,]/g, ""))} placeholder="px/m" inputMode="decimal" onKeyDown={(e) => { if (e.key === "Enter") applyScaleInput(); }} />
                <button className="hem__tool" onClick={applyScaleInput} title="Sätt skala direkt (design­enheter per meter)">Sätt skala</button>
              </span>
            )}
            {scale > 0 && <span className="hem__scale-badge">📏 {scale.toFixed(0)} px/m</span>}
            {showAreas && scale > 0 && roomAreas.length > 0 && (
              <span className="hem__area-sum">
                Uppskattad{estBalcony > 0 ? " (exkl. balkong)" : ""} <strong>{fmtM2(estLiving)}</strong>
                <button className="hem__tool" onClick={editActualArea} title="Ange riktig boarea">Faktisk: {actualArea > 0 ? fmtM2(actualArea) : "—"} ✎</button>
                {estBalcony > 0 && <span>Inkl. balkong <strong>{fmtM2(estInclBalcony)}</strong></span>}
              </span>
            )}
          </div>
          <div className="hem__palette">
            {FIXTURE_KINDS.map((k) => (
              <button
                key={k.key}
                className={`hem__pal-item${tool === `fix:${k.key}` ? " hem__pal-item--on" : ""}`}
                draggable
                onDragStart={(e) => e.dataTransfer.setData("hem-kind", k.key)}
                onClick={() => { setTool(`fix:${k.key}`); setWallStart(null); }}
                title={`Dra ut ${k.label} på planen`}
              >
                <span className="hem__pal-abbr">{k.abbr || k.label.slice(0, 3).toUpperCase()}</span>
                <span className="hem__pal-name">{k.label}</span>
              </button>
            ))}
          </div>
          {uploadMsg && <span className="hem__empty-hint">{uploadMsg}</span>}
          {tool.startsWith("fix:") && <span className="hem__empty-hint">Dra en pryl från paletten till planen — eller klicka här och sedan på planen för att placera {kindOf(tool.slice(4))?.label}.</span>}
          {tool === WALL_TOOL && <span className="hem__empty-hint">Klicka för att sätta väggens hörn, klicka vidare för att fortsätta. Esc avslutar ritläget.</span>}
          {tool === BALCONY_TOOL && <span className="hem__empty-hint">Rita balkongens väggar precis som vanliga väggar — dessa räknas inte med i m²-uppskattningen. Esc avslutar.</span>}
          {tool === DOOR_TOOL && <span className="hem__empty-hint">Klicka på de två väggändarna som dörröppningen sitter mellan (punkterna markeras). Dörren fäster i väggändarna och sluter rummet så ytan kan räknas ut{scale > 0 ? "" : " — sätt skala med 📏 för att få m²"}. Esc avbryter.</span>}
          {tool === SCALE_TOOL && <span className="hem__empty-hint">Klicka två punkter längs ett känt mått (t.ex. en vägg), så anger du hur många meter det är — sen matchar prylarnas cm-mått ritningen. Esc avbryter.</span>}
          {tool === WINDOW_TOOL && <span className="hem__empty-hint">Klicka på en vägg där fönstret börjar och sedan där det slutar — fönstret följer väggen. Klicka på ett befintligt fönster för att ta bort det. Fönster syns i 🧊 3D. Esc avbryter.</span>}
          {tool === MEASURE_TOOL && <span className="hem__empty-hint">Klicka två punkter (t.ex. mellan två väggar) för att mäta avståndet{scale > 0 ? " i meter" : " — sätt skala med 📏 för att få meter"}. Måtten sparas. Klicka på siffran för att skriva in ett riktigt mått, klicka på linjen för att ta bort. Esc avbryter.</span>}
        </div>
      )}

      {view3d === "split" && (
        <div className="hem__split3d">
          <React.Suspense fallback={<p className="hem__empty-hint">Laddar 3D…</p>}>
            <Hem3D
            walls={walls} windows={windows} fixtures={fixtures} labels={labels} scale={scale}
            wallColors={wallColors} doorsClosed={doorsClosed} setDoorsClosed={setDoorsClosedP}
            selectedId={selectedId} onSelect={select3d} onMoveFixture={moveFixture3d}
            onRotateFixture={rotateFixture3d} onPaint={paintWalls} onPaintFixture={paintFixture3d}
          />
          </React.Suspense>
        </div>
      )}
      <div className="hem__canvaswrap" ref={wrapRef} style={show3d ? { display: "none" } : undefined}>
        {/* View tools sit in a bar above the plan, not floating over it */}
        {!editMode && (
          <div className="hem__viewtools">
            <button
              className={`hem__vtool${tool === MEASURE_TOOL ? " hem__vtool--on" : ""}`}
              onClick={() => { setTool(tool === MEASURE_TOOL ? SELECT_TOOL : MEASURE_TOOL); setMeasureStart(null); }}
            >📐 Mät{tool === MEASURE_TOOL ? " (på)" : ""}</button>
            {measures.length > 0 && <button className="hem__vtool" onClick={() => setShowMeasures((v) => !v)} title={showMeasures ? "Dölj mått" : "Visa mått"}>{showMeasures ? "🙈" : "👁"}</button>}
            {measures.length > 0 && <button className="hem__vtool" onClick={() => { setMeasures((cur) => cur.filter((m) => !!(m.label && m.label.trim()))); setMeasureStart(null); }} title="Rensa mått utan inskrivet värde">🧹</button>}
            {roomAreas.length > 0 && scale > 0 && <button className={`hem__vtool${showAreas ? " hem__vtool--on" : ""}`} onClick={() => setAreas(!showAreas)} title={showAreas ? "Dölj rumsytor" : "Visa rumsytor"}>📐 m²</button>}
            {hasDoors && <button className={`hem__vtool${doorsClosed ? " hem__vtool--on" : ""}`} onClick={() => setDoorsClosedP(!doorsClosed)} title={doorsClosed ? "Visa dörrar öppna" : "Visa dörrar stängda"}>🚪</button>}
            {showAreas && scale > 0 && roomAreas.length > 0 && <span className="hem__vhint">Uppskattad {fmtM2(estLiving)}{actualArea > 0 ? ` · faktisk ${fmtM2(actualArea)}` : ""}{estBalcony > 0 ? ` · inkl balkong ${fmtM2(estInclBalcony)}` : ""}</span>}
            {tool === MEASURE_TOOL && <span className="hem__vhint">Klicka två punkter{scale > 0 ? " för meter" : ""}. Klicka på siffran = skriv in riktigt mått.</span>}
          </div>
        )}
        <svg
          ref={svgRef}
          className="hem__canvas"
          viewBox={`${view.x} ${view.y} ${view.w} ${view.h}`}
          preserveAspectRatio="xMidYMid meet"
          onPointerDown={onCanvasDown}
          onPointerMove={onCanvasMove}
          onPointerUp={onCanvasUp}
          onDragOver={(e) => { if (editMode) e.preventDefault(); }}
          onDrop={onCanvasDrop}
          style={{ height: planH ? `${planH}px` : undefined, width: planH ? `${(planH * PLAN_W) / PLAN_H}px` : undefined, cursor: tool === SELECT_TOOL ? "default" : "crosshair" }}
        >
          {planUrl && showPlan && <image href={planUrl} x="0" y="0" width={PLAN_W} height={PLAN_H} preserveAspectRatio="xMidYMid meet" className="hem__canvas-img" transform={planRot ? `rotate(${planRot} ${PLAN_W / 2} ${PLAN_H / 2})` : undefined} />}

          {walls.map((w) => {
            const wsel = selectedWallId === w.id;
            const mx = (w.x1 + w.x2) / 2, my = (w.y1 + w.y2) / 2;
            return (
              <g key={w.id} className={wsel ? "hem__wall-g--sel" : undefined}>
                {w.door
                  ? <DoorSymbol w={w} closed={doorsClosed} />
                  : (() => { const d = wallDraw[w.id] ?? w; return <line className={`hem__wall${w.balcony ? " hem__wall--balcony" : ""}${wsel ? " hem__wall--sel" : ""}`} x1={d.x1} y1={d.y1} x2={d.x2} y2={d.y2} />; })()}
                {editMode && !furnitureOnly && tool !== MEASURE_TOOL && (
                  <line
                    className="hem__wall-hit"
                    x1={w.x1} y1={w.y1} x2={w.x2} y2={w.y2}
                    style={{ cursor: tool === SELECT_TOOL ? "move" : "crosshair" }}
                    onPointerDown={(e) => onWallBodyDown(e, w)}
                    onDoubleClick={() => setWalls((cur) => cur.filter((x) => x.id !== w.id))}
                  />
                )}
                {editMode && !furnitureOnly && tool === SELECT_TOOL && wsel && (
                  <>
                    <circle className="hem__wall-handle" cx={w.x1} cy={w.y1} r={5} onPointerDown={(e) => onWallEndDown(e, w, "a")} />
                    <circle className="hem__wall-handle" cx={w.x2} cy={w.y2} r={5} onPointerDown={(e) => onWallEndDown(e, w, "b")} />
                    <g className="hem__fix-del" onPointerDown={(e) => { e.stopPropagation(); setWalls((cur) => cur.filter((x) => x.id !== w.id)); setSelectedWallId(null); }} onClick={(e) => e.stopPropagation()}>
                      <circle cx={mx} cy={my} r={9} />
                      <text x={mx} y={my} textAnchor="middle" dominantBaseline="central">×</text>
                    </g>
                  </>
                )}
              </g>
            );
          })}
          {wallStart && cursor && <line className="hem__wall-preview" x1={wallStart.x} y1={wallStart.y} x2={cursor.x} y2={cursor.y} />}
          {wallStart && <circle className="hem__wall-node" cx={wallStart.x} cy={wallStart.y} r={5} />}
          {tool === DOOR_TOOL && walls.map((w) => (
            <g key={`snap-${w.id}`}>
              <circle className="hem__door-snap" cx={w.x1} cy={w.y1} r={4} />
              <circle className="hem__door-snap" cx={w.x2} cy={w.y2} r={4} />
            </g>
          ))}
          {tool === DOOR_TOOL && doorStart && cursor && <line className="hem__door-preview" x1={doorStart.x} y1={doorStart.y} x2={cursor.x} y2={cursor.y} />}
          {tool === DOOR_TOOL && doorStart && <circle className="hem__wall-node" cx={doorStart.x} cy={doorStart.y} r={5} />}

          {/* windows: plan-style white gap in the wall with a thin glazing line */}
          {windows.map((wn) => (
            <g key={wn.id} className="hem__win">
              <line className="hem__win-gap" x1={wn.x1} y1={wn.y1} x2={wn.x2} y2={wn.y2} />
              <line className="hem__win-glass" x1={wn.x1} y1={wn.y1} x2={wn.x2} y2={wn.y2} />
              {editMode && (tool === WINDOW_TOOL || (tool === SELECT_TOOL && !furnitureOnly)) && !winStart && (
                <line
                  className="hem__win-hit"
                  x1={wn.x1} y1={wn.y1} x2={wn.x2} y2={wn.y2}
                  onPointerDown={(e) => { e.stopPropagation(); setWindows((cur) => cur.filter((x) => x.id !== wn.id)); }}
                ><title>Ta bort fönster</title></line>
              )}
            </g>
          ))}
          {tool === WINDOW_TOOL && winStart && cursor && <line className="hem__win-preview" x1={winStart.x} y1={winStart.y} x2={cursor.x} y2={cursor.y} />}
          {tool === WINDOW_TOOL && winStart && <circle className="hem__wall-node" cx={winStart.x} cy={winStart.y} r={5} />}
          {tool === SCALE_TOOL && scaleStart && cursor && <line className="hem__scale-line" x1={scaleStart.x} y1={scaleStart.y} x2={cursor.x} y2={cursor.y} />}
          {tool === SCALE_TOOL && scaleStart && <circle className="hem__wall-node" cx={scaleStart.x} cy={scaleStart.y} r={5} />}

          {labels.map((l) => (
            <text
              key={l.id}
              className="hem__canvas-label"
              x={l.x} y={l.y}
              onDoubleClick={() => {
                if (!editMode) return;
                const t = window.prompt("Ändra text (tomt = ta bort):", l.text);
                if (t === null) return;
                if (t.trim() === "") setLabels((cur) => cur.filter((x) => x.id !== l.id));
                else setLabels((cur) => cur.map((x) => (x.id === l.id ? { ...x, text: t.trim() } : x)));
              }}
            >{l.text}</text>
          ))}

          {/* overlay kinds (worktops) render last so they sit on top, outline-only */}
          {[...fixtures].sort((a, b) => (OVERLAY.has(a.kind) ? 1 : 0) - (OVERLAY.has(b.kind) ? 1 : 0)).map((f) => {
            const st = replacementStatus(f);
            return (
              <g
                key={f.id}
                className={`hem__fix hem__fix--${st}${selectedId === f.id ? " hem__fix--sel" : ""}`}
                transform={`translate(${f.x} ${f.y})`}
                style={{ pointerEvents: tool === MEASURE_TOOL ? "none" : undefined }}
                onPointerDown={(e) => onFixtureDown(e, f)}
                onClick={(e) => { e.stopPropagation(); if (!editMode) setSelectedId(f.id); }}
                onDoubleClick={(e) => { e.stopPropagation(); if (editMode) setEditing(f); else setViewing(f); }}
              >
                <g transform={`rotate(${f.rot || 0} ${f.w / 2} ${f.h / 2})`}>
                  {!NO_BOX.has(f.kind) && <rect className={`hem__fix-box${OVERLAY.has(f.kind) ? " hem__fix-box--overlay" : ""}`} x={0} y={0} width={f.w} height={f.h} rx={2} />}
                  <FixtureSymbol f={f} />
                </g>
                {editMode && tool === SELECT_TOOL && selectedId === f.id && (
                  <>
                    <g className="hem__fix-del" onPointerDown={(e) => { e.stopPropagation(); deleteFixture(f.id); }} onClick={(e) => e.stopPropagation()}>
                      <circle cx={f.w} cy={0} r={9} />
                      <text x={f.w} y={0} textAnchor="middle" dominantBaseline="central">×</text>
                    </g>
                    <g className="hem__fix-rot" onPointerDown={(e) => { e.stopPropagation(); updateFixture(f.id, { rot: (((f.rot || 0) + 90) % 360) }); }} onClick={(e) => e.stopPropagation()}>
                      <circle cx={0} cy={0} r={9} />
                      <text x={0} y={0} textAnchor="middle" dominantBaseline="central">↻</text>
                    </g>
                  </>
                )}
              </g>
            );
          })}

          {/* per-room area labels (needs a calibrated scale to show m²) */}
          {showAreas && scale > 0 && roomAreas
            .filter((r) => r.areaUnits / (scale * scale) >= MIN_ROOM_M2)
            .map((r) => (
              <text key={r.id} className="hem__room-area" x={r.cx} y={r.cy} textAnchor="middle" dominantBaseline="central" pointerEvents="none">
                {(r.areaUnits / (scale * scale)).toFixed(1).replace(".", ",")} m²
              </text>
            ))}

          {/* open wall ends: gaps that stop a room from being detected */}
          {editMode && openEnds.map((p, i) => (
            <circle key={`open-${i}`} className="hem__open-end" cx={p.x} cy={p.y} r={6} pointerEvents="none" />
          ))}

          {/* measurements render last so they sit on top of walls & furniture */}
          {showMeasures && measures.map((mm) => {
            const midx = (mm.x1 + mm.x2) / 2, midy = (mm.y1 + mm.y2) / 2;
            const canDelete = tool === SELECT_TOOL || (tool === MEASURE_TOOL && !measureStart);
            const ta = tickEnds(mm.x1, mm.y1, mm.x2, mm.y2, mm.x1, mm.y1);
            const tb = tickEnds(mm.x1, mm.y1, mm.x2, mm.y2, mm.x2, mm.y2);
            return (
              <g key={mm.id} className={`hem__measure${mm.label ? " hem__measure--manual" : ""}`}>
                <line className="hem__measure-line" x1={mm.x1} y1={mm.y1} x2={mm.x2} y2={mm.y2} />
                <line className="hem__measure-end" x1={ta.x1} y1={ta.y1} x2={ta.x2} y2={ta.y2} />
                <line className="hem__measure-end" x1={tb.x1} y1={tb.y1} x2={tb.x2} y2={tb.y2} />
                <line
                  className="hem__measure-hit"
                  x1={mm.x1} y1={mm.y1} x2={mm.x2} y2={mm.y2}
                  onPointerDown={(e) => { if (canDelete) { e.stopPropagation(); setMeasures((cur) => cur.filter((x) => x.id !== mm.id)); } }}
                />
                <text
                  className="hem__measure-label"
                  x={midx} y={midy - 5} textAnchor="middle" dominantBaseline="central"
                  onPointerDown={(e) => { e.stopPropagation(); editMeasure(mm); }}
                >{measureText(mm)}</text>
              </g>
            );
          })}
          {tool === MEASURE_TOOL && measureStart && cursor && (() => {
            const ta = tickEnds(measureStart.x, measureStart.y, cursor.x, cursor.y, measureStart.x, measureStart.y);
            const tb = tickEnds(measureStart.x, measureStart.y, cursor.x, cursor.y, cursor.x, cursor.y);
            return (
              <g className="hem__measure">
                <line className="hem__measure-line" x1={measureStart.x} y1={measureStart.y} x2={cursor.x} y2={cursor.y} />
                <line className="hem__measure-end" x1={ta.x1} y1={ta.y1} x2={ta.x2} y2={ta.y2} />
                <line className="hem__measure-end" x1={tb.x1} y1={tb.y1} x2={tb.x2} y2={tb.y2} />
                <text className="hem__measure-label" x={(measureStart.x + cursor.x) / 2} y={(measureStart.y + cursor.y) / 2 - 5} textAnchor="middle" dominantBaseline="central">{mLabel(Math.hypot(cursor.x - measureStart.x, cursor.y - measureStart.y))}</text>
              </g>
            );
          })()}
          {tool === MEASURE_TOOL && measureStart && <circle className="hem__wall-node" cx={measureStart.x} cy={measureStart.y} r={5} />}
        </svg>
        <div className="hem__zoom">
          <button className="hem__zoom-btn" onClick={() => zoomCenter(0.8)} title="Zooma in" aria-label="Zooma in">+</button>
          <span className="hem__zoom-pct">{zoomPct}%</span>
          <button className="hem__zoom-btn" onClick={() => zoomCenter(1.25)} title="Zooma ut" aria-label="Zooma ut">−</button>
          <button className="hem__zoom-btn" onClick={resetView} title="Återställ" aria-label="Återställ zoom">⤢</button>
        </div>
      </div>
      </div>

      {!editMode && selected && (
        <div className="hem__detail">
          <div className="hem__item hem__item--static">
            <span className="hem__mono">{abbrForFixture(selected)}</span>
            <span className="hem__item-main">
              <span className="hem__item-name">{selected.label}</span>
              {[selected.brand, selected.model].filter(Boolean).length > 0 && <span className="hem__item-meta">{[selected.brand, selected.model].filter(Boolean).join(" ")}</span>}
              {selected.notes && <span className="hem__item-notes">{selected.notes}</span>}
            </span>
            <span className="hem__item-side">
              {selected.lastReplaced && <span className="hem__item-replaced">Bytt {selected.lastReplaced}</span>}
              {dueLine(selected) && <span className={`hem__badge hem__badge--${replacementStatus(selected)}`}>{STATUS_LABEL[replacementStatus(selected)]} · {dueLine(selected)}</span>}
            </span>
          </div>
        </div>
      )}

      {fixtures.length === 0 && walls.length === 0 && !editMode && (
        <p className="hem__all-good">Tomt än. Tryck ✏️ Redigera för att rita väggar och lägga till inredning.</p>
      )}
      </div>

      {editing && (
        <FixtureEditor
          initial={editing}
          scale={scale}
          onSave={(f) => { updateFixture(f.id, f); setEditing(null); }}
          onDelete={() => deleteFixture(editing.id)}
          onCancel={() => setEditing(null)}
        />
      )}

      {moodOpen && (
        <Moodboards ownerUid={activeOwner} rooms={rooms} setRooms={setRooms} planLabels={labels.map((l) => l.text)} onClose={() => setMoodOpen(false)} />
      )}

      {shareOpen && (
        <div className="hem__overlay" onClick={() => setShareOpen(false)}>
          <div className="hem__modal" onClick={(e) => e.stopPropagation()}>
            <div className="hem__modal-head">
              <h3 className="hem__modal-title">👥 Dela hemmet</h3>
              <button className="hem__modal-close" onClick={() => setShareOpen(false)} aria-label="Stäng">×</button>
            </div>
            <div className="hem__modal-body">
              <span className="hem__empty-hint">Bjud in någon med deras e-post. De kan öppna och redigera hemmet när de loggar in med samma adress.</span>
              <div className="hem__form-row">
                <input className="hem__input" type="email" value={shareInput} placeholder="namn@exempel.se" onChange={(e) => setShareInput(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") addShare(); }} />
                <button className="hem__btn" onClick={addShare}>Dela</button>
              </div>
              {shareMsg && <span className="hem__empty-hint">{shareMsg}</span>}
              {sharedWith.length > 0 ? (
                <div className="hem__info">
                  {sharedWith.map((e) => (
                    <div key={e} className="hem__info-row">
                      <span>{e}</span>
                      <button className="hem__link-btn hem__link-btn--danger" onClick={() => removeShare(e)}>Ta bort</button>
                    </div>
                  ))}
                </div>
              ) : <span className="hem__empty-hint">Inte delad med någon ännu.</span>}
            </div>
            <div className="hem__modal-foot">
              <span className="hem__foot-spacer" />
              <button className="hem__btn" onClick={() => setShareOpen(false)}>Klar</button>
            </div>
          </div>
        </div>
      )}

      {viewing && (
        <div className="hem__overlay" onClick={() => setViewing(null)}>
          <div className="hem__modal" onClick={(e) => e.stopPropagation()}>
            <div className="hem__modal-head">
              <h3 className="hem__modal-title">{viewing.label || "Inredning"}</h3>
              <button className="hem__modal-close" onClick={() => setViewing(null)} aria-label="Stäng">×</button>
            </div>
            <div className="hem__modal-body">
              <div className="hem__info">
                <div className="hem__info-row"><span>Typ</span><b>{kindOf(viewing.kind)?.label || viewing.label}</b></div>
                {(viewing.brand || viewing.model) && <div className="hem__info-row"><span>Märke/modell</span><b>{[viewing.brand, viewing.model].filter(Boolean).join(" ")}</b></div>}
                {viewing.lastReplaced && <div className="hem__info-row"><span>Senast bytt</span><b>{viewing.lastReplaced}</b></div>}
                {viewing.intervalMonths && <div className="hem__info-row"><span>Bytesintervall</span><b>{viewing.intervalMonths} mån</b></div>}
                {dueLine(viewing) && <div className="hem__info-row"><span>Status</span><b className={`hem__badge hem__badge--${replacementStatus(viewing)}`}>{STATUS_LABEL[replacementStatus(viewing)]} · {dueLine(viewing)}</b></div>}
                {scale > 0 && <div className="hem__info-row"><span>Mått</span><b>{Math.round((viewing.w / scale) * 100)} × {Math.round((viewing.h / scale) * 100)} cm</b></div>}
                {ELEV_KINDS.has(viewing.kind) && <div className="hem__info-row"><span>Höjd från golvet</span><b>{elevOf(viewing)} cm</b></div>}
                {viewing.notes && <div className="hem__info-row"><span>Anteckningar</span><b>{viewing.notes}</b></div>}
              </div>
            </div>
            <div className="hem__modal-foot">
              <button className="hem__link-btn" onClick={() => { setViewing(null); toggleEdit(); setSelectedId(viewing.id); setEditing(viewing); }}>✏️ Redigera</button>
              <span className="hem__foot-spacer" />
              <button className="hem__btn" onClick={() => setViewing(null)}>Stäng</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const FixtureEditor = ({
  initial, scale, onSave, onDelete, onCancel,
}: {
  initial: Fixture;
  scale: number;
  onSave: (f: Fixture) => void;
  onDelete: () => void;
  onCancel: () => void;
}) => {
  const [f, setF] = useState<Fixture>(initial);
  const set = (patch: Partial<Fixture>) => setF((cur) => ({ ...cur, ...patch }));
  const trackable = kindOf(f.kind)?.track !== false;
  const cm = scale > 0;
  const toCm = (u: number) => Math.round((u / scale) * 100);
  const fromCm = (v: number) => Math.max(4, Math.round((v / 100) * scale));
  // Mirror the dimension fields as free text; normalise on blur/save so a value
  // can be cleared and retyped smaller without clamping on every keystroke.
  const [wStr, setWStr] = useState(String(cm ? toCm(f.w) : f.w));
  const [hStr, setHStr] = useState(String(cm ? toCm(f.h) : f.h));
  const toUnits = (raw: string, cur: number): number => {
    const n = parseFloat(raw.replace(",", "."));
    if (!isFinite(n) || n <= 0) return cur;
    return cm ? fromCm(n) : Math.max(4, Math.round(n));
  };
  const blurW = () => { const u = toUnits(wStr, f.w); set({ w: u }); setWStr(String(cm ? toCm(u) : u)); };
  const blurH = () => { const u = toUnits(hStr, f.h); set({ h: u }); setHStr(String(cm ? toCm(u) : u)); };
  const hasElev = ELEV_KINDS.has(f.kind);
  const [elevStr, setElevStr] = useState(String(elevOf(f)));
  const toElev = (raw: string): number => {
    const n = parseFloat(raw.replace(",", "."));
    return isFinite(n) ? Math.max(0, Math.min(300, Math.round(n))) : elevOf(f);
  };
  const blurElev = () => { const v = toElev(elevStr); set({ elev: v }); setElevStr(String(v)); };
  const handleSave = () => onSave({
    ...f, w: toUnits(wStr, f.w), h: toUnits(hStr, f.h),
    ...(hasElev ? { elev: toElev(elevStr) } : {}),
  });
  return (
    <div className="hem__overlay" onClick={onCancel}>
      <div className="hem__modal" onClick={(e) => e.stopPropagation()}>
        <div className="hem__modal-head">
          <h3 className="hem__modal-title">{f.label || "Inredning"}</h3>
          <button className="hem__modal-close" onClick={onCancel} aria-label="Stäng">×</button>
        </div>
        <div className="hem__modal-body">
          <label className="hem__field">
            <span>Typ</span>
            <div className="hem__kindgrid">
              {FIXTURE_KINDS.map((k) => (
                <button key={k.key} type="button" className={`hem__kind-btn${f.kind === k.key ? " hem__kind-btn--on" : ""}`}
                  onClick={() => set({ kind: k.key, label: f.label === kindOf(f.kind)?.label ? k.label : f.label })}>{k.label}</button>
              ))}
            </div>
          </label>
          <input className="hem__input" value={f.label} onChange={(e) => set({ label: e.target.value })} placeholder="Etikett (t.ex. Kylskåp, Garderob)" />
          {trackable && (
            <>
              <div className="hem__form-row">
                <input className="hem__input" value={f.brand} onChange={(e) => set({ brand: e.target.value })} placeholder="Märke" />
                <input className="hem__input" value={f.model} onChange={(e) => set({ model: e.target.value })} placeholder="Modell" />
              </div>
              <div className="hem__form-row">
                <label className="hem__field"><span>Senast bytt</span><input className="hem__input" type="month" value={f.lastReplaced.slice(0, 7)} onChange={(e) => set({ lastReplaced: e.target.value })} /></label>
                <label className="hem__field"><span>Bytesintervall (mån)</span><input className="hem__input" value={f.intervalMonths} onChange={(e) => set({ intervalMonths: e.target.value.replace(/[^0-9]/g, "") })} placeholder="t.ex. 12" /></label>
              </div>
            </>
          )}
          <textarea className="hem__textarea" value={f.notes} onChange={(e) => set({ notes: e.target.value })} placeholder="Anteckningar (valfritt)" />
          <div className="hem__form-row">
            <label className="hem__field"><span>{cm ? "Bredd (cm)" : "Bredd"}</span>
              <input className="hem__input" type="number" inputMode="decimal" value={wStr} onChange={(e) => setWStr(e.target.value)} onBlur={blurW} /></label>
            <label className="hem__field"><span>{cm ? "Djup (cm)" : "Höjd"}</span>
              <input className="hem__input" type="number" inputMode="decimal" value={hStr} onChange={(e) => setHStr(e.target.value)} onBlur={blurH} /></label>
          </div>
          {hasElev && (
            <label className="hem__field"><span>Höjd från golvet (cm)</span>
              <input className="hem__input" type="number" inputMode="numeric" value={elevStr} onChange={(e) => setElevStr(e.target.value)} onBlur={blurElev} /></label>
          )}
          {scale <= 0 && <span className="hem__empty-hint">Tips: kalibrera med 📏 Skala så kan du sätta mått i riktiga cm.</span>}
          <label className="hem__field">
            <span>Rotation</span>
            <div className="hem__rot-row">
              <button type="button" className="hem__btn hem__btn--ghost" onClick={() => set({ rot: (((f.rot || 0) + 90) % 360) })}>↻ Rotera 90°</button>
              <span className="hem__rot-val">{f.rot || 0}°</span>
            </div>
          </label>
        </div>
        <div className="hem__modal-foot">
          <button className="hem__link-btn hem__link-btn--danger" onClick={onDelete}>Ta bort</button>
          <span className="hem__foot-spacer" />
          <button className="hem__btn hem__btn--ghost" onClick={onCancel}>Avbryt</button>
          <button className="hem__btn" onClick={handleSave}>Spara</button>
        </div>
      </div>
    </div>
  );
};
